import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { Loader } from "@package/subframe/components/Loader";
import { FeatherClock } from "@subframe/core";
import { Link } from "@tanstack/react-router";
import { useMemo } from "react";

import { useListActionsByNurseryQueryControllerListByNursery } from "@/generated/follow-up-actions/follow-up-actions";
import type { FollowUpActionResponseDto } from "@/generated/model";
import {
  formatActionDueDate,
  getActionStatusBadge,
  isActionOverdue,
} from "@/lib/follow-up-action-utils";
import { NURSERY_ROLE_LABELS } from "@/lib/roles";

interface LinkedFollowUpActionsProps {
  nurseryId: string;
  incidentId: string;
}

/**
 * Lists follow-up actions linked to an incident.
 *
 * Implementation note: we filter client-side off the existing
 * list-by-nursery endpoint rather than adding a `findByIncidentId` query.
 * The dashboard's FollowUpActionsTab already pulls this list, so the data is
 * usually warm in React Query's cache; volume per nursery is low enough that
 * no extra backend endpoint is justified for v2.
 */
export function LinkedFollowUpActions({
  nurseryId,
  incidentId,
}: LinkedFollowUpActionsProps) {
  const { data, isLoading } =
    useListActionsByNurseryQueryControllerListByNursery(
      nurseryId,
      { dashboardView: true },
      { query: { enabled: !!nurseryId, refetchOnMount: "always" } },
    );

  const linked = useMemo(() => {
    const actions =
      (data?.data as FollowUpActionResponseDto[] | undefined) ?? [];
    return actions.filter((a) => a.outOfRangeIncidentId === incidentId);
  }, [data?.data, incidentId]);

  if (isLoading) {
    return (
      <section className="flex w-full flex-col items-start gap-3">
        <span className="text-heading-3 font-heading-3 text-default-font">
          Linked follow-up tasks
        </span>
        <div className="flex w-full items-center justify-center py-4">
          <Loader size="medium" />
        </div>
      </section>
    );
  }

  if (linked.length === 0) {
    // Stay silent when nothing is linked — the "Create follow-up task" button
    // is the entry point and a permanent empty card would just be noise.
    return null;
  }

  return (
    <section className="flex w-full flex-col items-start gap-3">
      <div className="flex w-full items-center gap-2">
        <span className="text-heading-3 font-heading-3 text-default-font">
          Linked follow-up tasks
        </span>
        <Badge variant="neutral">{linked.length}</Badge>
      </div>
      <ul className="flex w-full flex-col gap-2">
        {linked.map((action) => (
          <li key={action.id} className="w-full">
            <Link
              to="/follow-up-actions/$actionId"
              params={{ actionId: action.id }}
              className="flex w-full items-center justify-between gap-4 rounded-md border border-neutral-200 bg-white px-4 py-3 hover:border-neutral-400 hover:shadow-sm transition"
            >
              <LinkedActionSummary action={action} />
              <div className="flex flex-shrink-0 items-center gap-2">
                {getActionStatusBadge(action.status)}
                <Button variant="neutral-primary">View</Button>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function LinkedActionSummary({
  action,
}: {
  action: FollowUpActionResponseDto;
}) {
  const dueDateText = formatActionDueDate(action);
  const overdue = isActionOverdue(action);
  const assignee = action.assignee
    ? [action.assignee.firstName, action.assignee.lastName]
        .filter(Boolean)
        .join(" ") || action.assignee.email
    : action.assigneeRole
      ? NURSERY_ROLE_LABELS[action.assigneeRole] || action.assigneeRole
      : null;

  return (
    <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1 min-w-0">
      <span className="text-body-bold font-body-bold text-neutral-900 truncate w-full">
        {action.description}
      </span>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {dueDateText ? (
          <span
            className={`flex items-center gap-1 text-caption font-caption ${overdue ? "text-error-700 font-caption-bold" : "text-subtext-color"}`}
          >
            <FeatherClock className="h-3 w-3" />
            {dueDateText}
          </span>
        ) : (
          <span className="text-caption font-caption text-subtext-color">
            No due date
          </span>
        )}
        {assignee && (
          <span className="text-caption font-caption text-subtext-color">
            · {assignee}
          </span>
        )}
      </div>
    </div>
  );
}
